import React, { useMemo, useState } from 'react';
import { Card, Button } from './ui';
import { ThermalCheckin } from '../types';
import { useNavigate } from 'react-router-dom';

interface ThermalHeatmapProps {
    checkins: ThermalCheckin[];
}

type DiagnosticState = 'Green' | 'Yellow' | 'Orange' | 'Red';

const stateSeverity = {
    'Red': 4,
    'Orange': 3,
    'Yellow': 2,
    'Green': 1
};

const getStateColor = (state: DiagnosticState | null, isBackground = false) => {
    switch (state) {
        case 'Green': return isBackground ? 'bg-emerald-100 text-emerald-900' : 'bg-emerald-600 text-white shadow-sm';
        case 'Yellow': return isBackground ? 'bg-amber-100 text-amber-900' : 'bg-amber-400 text-amber-950 shadow-sm';
        case 'Orange': return isBackground ? 'bg-orange-100 text-orange-900' : 'bg-orange-500 text-white shadow-sm';
        case 'Red': return isBackground ? 'bg-rose-100 text-rose-900' : 'bg-rose-600 text-white shadow-sm';
        default: return isBackground ? 'bg-md-surface-variant' : 'bg-md-surface-container text-md-on-surface-variant border border-md-outline-variant';
    }
};

const getStateIcon = (state: DiagnosticState | null) => {
    switch (state) {
        case 'Green': return '🌿';
        case 'Yellow': return '⚡';
        case 'Orange': return '⚠️';
        case 'Red': return '🚨';
        default: return '—';
    }
};

export const ThermalHeatmap: React.FC<ThermalHeatmapProps> = ({ checkins }) => {
    // 7-day chronological data points
    const { chartData, insights } = useMemo(() => {
        const days = 7;
        const result = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let totalValidCheckins = 0;
        let redDays = 0;
        let orangeDays = 0;
        let yellowDays = 0;
        let greenDays = 0;

        for (let i = days - 1; i >= 0; i--) {
            const targetDate = new Date(today);
            targetDate.setDate(today.getDate() - i);
            const dateString = targetDate.toLocaleDateString('en-GB', { weekday: 'short' });
            const isToday = i === 0;

            // Find checkins for this specific day
            const daysCheckins = checkins.filter(c => {
                const cDate = new Date(c.timestamp);
                return cDate.getDate() === targetDate.getDate() && 
                       cDate.getMonth() === targetDate.getMonth() && 
                       cDate.getFullYear() === targetDate.getFullYear();
            });

            let worstState: DiagnosticState | null = null;
            let worstSeverity = 0;

            if (daysCheckins.length > 0) {
                totalValidCheckins++;
                
                daysCheckins.forEach(c => {
                    let cState: DiagnosticState = 'Green';
                    
                    if (c.diagnosticState) {
                        cState = c.diagnosticState as DiagnosticState;
                    } else if (c.value !== undefined) {
                        // Legacy fallback mapping
                        if (c.value >= 4) cState = 'Red';
                        else if (c.value >= 2) cState = 'Orange';
                        else if (c.value >= 1) cState = 'Yellow';
                        else cState = 'Green';
                    }

                    const severity = stateSeverity[cState];
                    if (severity > worstSeverity) {
                        worstSeverity = severity;
                        worstState = cState;
                    }
                });

                if (worstState === 'Red') redDays++;
                if (worstState === 'Orange') orangeDays++;
                if (worstState === 'Yellow') yellowDays++;
                if (worstState === 'Green') greenDays++;
            }

            result.push({
                index: days - 1 - i,
                day: isToday ? 'Today' : dateString,
                fullDate: targetDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                state: worstState,
                isToday,
                rawCheckins: daysCheckins
            });
        }

        // Generate Insights
        const generatedInsights = [];
        
        if (totalValidCheckins === 0) {
            generatedInsights.push({
                title: "Run a System Check",
                desc: "Your timeline is empty. Complete a System Check to start mapping your week.",
                icon: "⏱️",
                color: "#64748b",
                action: { label: "Log an engine check", path: "#" }
            });
        } else {
            if (redDays >= 2) {
                generatedInsights.push({
                    title: "Critical Overload",
                    desc: `You've hit the Red zone ${redDays} times this week. Please don't carry this alone. Reach out for support or speak to a Buddy.`,
                    icon: "🚨",
                    color: "#e11d48",
                    action: { label: "Connect to a Buddy", path: "/buddy-matcher" }
                });
            } else if (redDays + orangeDays >= 3) {
                generatedInsights.push({
                    title: "High System Load",
                    desc: "You've been tracking hot for several days. Prioritize cooldown exercises and boundary setting.",
                    icon: "⚠️",
                    color: "#ea580c",
                    action: { label: "Review Foundation Course", path: "/course/mindset_shift" }
                });
            } else if (greenDays >= 4 && redDays === 0 && orangeDays === 0) {
                generatedInsights.push({
                    title: "Steady Operation",
                    desc: "You're demonstrating strong emotional regulation this week. Excellent work maintaining balance.",
                    icon: "🌿",
                    color: "#059669",
                    action: { label: "Share a win", path: "/forum" }
                });
            } else if (totalValidCheckins < 4) {
                 generatedInsights.push({
                    title: "Incomplete Diagnostics",
                    desc: "Check in more regularly to get better insights into your weekly rhythm.",
                    icon: "📊",
                    color: "#4f46e5"
                });
            } else {
                 generatedInsights.push({
                    title: "Variable Load",
                    desc: "Your capacity is fluctuating. Pay close attention to the triggers right before your load increases.",
                    icon: "📈",
                    color: "#d97706",
                    action: { label: "Read Peer Stories", path: "/forum" }
                });
            }
        }

        return { chartData: result, insights: generatedInsights };
    }, [checkins]);

    const [selectedDay, setSelectedDay] = useState<any | null>(null);
    const navigate = useNavigate();

    return (
        <Card className="p-6 md:p-8 bg-md-surface border-0 shadow-sm relative overflow-hidden flex flex-col md:flex-row gap-8">
            <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-full mb-8 relative">
                    <h3 className="text-2xl font-black text-md-on-surface tracking-tight font-heading">System History</h3>
                    <p className="text-sm text-md-on-surface-variant font-medium">Your 7-day diagnostic timeline</p>
                </div>

                <div className="w-full flex items-end justify-between gap-1 sm:gap-2 px-2 max-w-xl mx-auto h-32 relative">
                    {/* Connecting background line */}
                    <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-slate-100 -z-10 rounded-full"></div>

                    {chartData.map((day, i) => (
                        <div 
                            key={i} 
                            className="flex flex-col items-center gap-3 flex-1 group cursor-pointer"
                            onClick={() => setSelectedDay(day)}
                        >
                            <div 
                                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-[16px] flex items-center justify-center text-lg transition-transform duration-300 group-hover:-translate-y-1 hover:shadow-md ${getStateColor(day.state)}`}
                                title="Click to view details"
                            >
                                {getStateIcon(day.state)}
                            </div>
                            
                            <div className="flex flex-col items-center">
                                <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider ${day.isToday ? 'text-indigo-600' : 'text-slate-400'}`}>
                                    {day.day}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="mt-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-center flex items-center gap-4 justify-center">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Green</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span> Yellow</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> Orange</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-600"></span> Red</span>
                </div>
            </div>

            {/* Right Side: Insights */}
            <div className="flex-1 w-full md:w-64 border-l border-slate-100 md:pl-8">
                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                    Pattern Insights
                </h4>
                
                <div className="space-y-3 relative z-20">
                    {insights.map((insight, idx) => (
                        <div key={idx} className="bg-md-surface-container-low rounded-[24px] p-5 border-0 transition-all hover:shadow-sm hover:bg-md-surface-variant group/insight relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 rounded-bl-full opacity-10" style={{ backgroundColor: insight.color }}></div>
                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0 bg-md-surface shadow-sm" style={{ color: insight.color }}>
                                    {insight.icon}
                                </div>
                                <div>
                                    <h5 className="font-bold text-slate-900 text-sm mb-1">{insight.title}</h5>
                                    <p className="text-xs text-slate-600 font-medium leading-relaxed">
                                        {insight.desc}
                                    </p>
                                    {insight.action && insight.action.path !== "#" && (
                                        <button 
                                            onClick={() => navigate(insight.action.path)}
                                            className="mt-3 text-[10px] uppercase tracking-widest font-black inline-flex items-center gap-1 hover:opacity-70 transition-opacity" style={{ color: insight.color }}
                                        >
                                            {insight.action.label} <span>→</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Daily Detail Modal */}
            {selectedDay && (
                <div className="absolute inset-0 bg-md-on-surface/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity" onClick={() => setSelectedDay(null)}>
                    <div 
                        className="bg-md-surface-container rounded-[32px] shadow-lg w-full max-w-lg flex flex-col animate-scale-up overflow-hidden" 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 sm:px-5 border-b border-slate-100 bg-white">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">{selectedDay.day === 'Today' ? 'Today' : selectedDay.fullDate}</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                    {selectedDay.rawCheckins.length} {selectedDay.rawCheckins.length === 1 ? 'System Log' : 'System Logs'}
                                </p>
                            </div>
                            <button 
                                onClick={() => setSelectedDay(null)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                        
                        {/* Horizontal scrolling, compact layout */}
                        <div className="p-4 sm:p-5 flex gap-4 overflow-x-auto snap-x hide-scrollbar items-start">
                            {selectedDay.rawCheckins.length === 0 ? (
                                <div className="text-center py-6 w-full">
                                    <div className="text-4xl mb-2 opacity-50">📭</div>
                                    <p className="text-slate-500 font-medium text-sm">No checks were logged on this day.</p>
                                </div>
                            ) : (
                                selectedDay.rawCheckins.map((checkin: any, idx: number) => {
                                    const timeStr = new Date(checkin.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                    const cState = checkin.diagnosticState || 'Unknown';
                                    
                                    let wordsList: string[] = [];
                                    if (Array.isArray(checkin.words)) wordsList = checkin.words;
                                    else if (typeof checkin.words === 'string') wordsList = checkin.words.split(',').map((w: string) => w.trim());

                                    return (
                                        <div key={checkin.id || idx} className="rounded-[24px] border border-md-outline-variant overflow-hidden shadow-sm shrink-0 w-full sm:w-[320px] snap-center bg-md-surface">
                                            {/* Header */}
                                            <div className={`px-5 py-3 flex items-center justify-between ${getStateColor(cState as DiagnosticState, true)} border-b border-md-outline-variant/30`}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xl drop-shadow-sm">{getStateIcon(cState as DiagnosticState)}</span>
                                                    <span className="font-bold text-sm tracking-tight">{cState}</span>
                                                </div>
                                                <span className="text-[10px] font-bold opacity-80 uppercase tracking-widest">{timeStr}</span>
                                            </div>
                                            
                                            {/* Body */}
                                            <div className="p-3 bg-slate-50/50 space-y-3">
                                                {/* Qualitative Data */}
                                                <div className="grid grid-cols-2 gap-3">
                                                    {checkin.weather && (
                                                        <div>
                                                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Weather</div>
                                                            <div className="text-xs font-medium text-slate-700 flex items-center gap-1.5 bg-white px-2 py-1.5 rounded-lg border border-slate-100 shadow-sm">
                                                                {checkin.weather.includes('Thunderstorm') ? '⛈️ Thunderstorm' :
                                                                 checkin.weather.includes('Clear') ? '☀️ Clear' :
                                                                 checkin.weather.includes('Foggy') ? '🌫️ Foggy' :
                                                                 checkin.weather.includes('Heat') ? '🔥 High Heat' :
                                                                 checkin.weather.includes('Overcast') ? '☁️ Overcast' : checkin.weather}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {checkin.battery !== undefined && (
                                                        <div>
                                                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Battery</div>
                                                            <div className="flex flex-col justify-center gap-1 bg-white px-2 py-1.5 rounded-lg border border-slate-100 shadow-sm">
                                                                <div className="flex items-center justify-between w-full">
                                                                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden mr-2">
                                                                        <div 
                                                                            className={`h-full rounded-full ${checkin.battery > 50 ? 'bg-emerald-500' : checkin.battery > 20 ? 'bg-amber-400' : 'bg-rose-500'}`} 
                                                                            style={{ width: `${checkin.battery}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-[10px] font-bold text-slate-700 leading-none">{checkin.battery}%</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Words */}
                                                {wordsList.length > 0 && (
                                                    <div>
                                                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Words</div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {wordsList.map((word, wIdx) => (
                                                                <span key={wIdx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold border border-indigo-100 shadow-sm">
                                                                    {word}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};
